import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { produce } from 'immer';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  cacheUtils,
  eluveAdminHasuraContext,
  useCompleteFragment,
} from '@eluve/apollo-client';
import { useToast } from '@eluve/components';
import { useUserIdFromSession } from '@eluve/session-helpers';

import { AddUserToTenantMutation } from '../tenant/$tenantId/users/operations';
import { usersTenantsFragment } from '../tenant/components/TenantActionsColumn';

import { getCommandPaletteUserTenantsQuery } from './operations';

const MAX_TENANTS_PER_USER = 20;

export type ProvisionStatus =
  | 'idle'
  | 'provisioning'
  | 'switching'
  | 'error'
  | 'done';

type RunArgs = {
  tenantId: string;
  targetPath: string;
};

export type UseProvisionAndNavigateResult = {
  run: (args: RunArgs) => Promise<void>;
  status: ProvisionStatus;
  error: string | null;
  atCapacity: boolean;
  isMember: (tenantId: string) => boolean;
};

export const useProvisionAndNavigate = (): UseProvisionAndNavigateResult => {
  const userId = useUserIdFromSession();
  const apiClient = useApiClient();
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const toast = useToast();

  // The palette mounts on every admin page via AdminLayout, but the fragment
  // is only seeded by the AdminTenants page's own query — so fire the same
  // query here to ensure `isMember`/`atCapacity` are accurate everywhere the
  // palette is reachable. Apollo dedupes against the AdminTenants query.
  useQuery(getCommandPaletteUserTenantsQuery, {
    variables: { userId },
    context: eluveAdminHasuraContext,
    skip: !userId,
  });

  const usersTenants = useCompleteFragment({
    fragment: usersTenantsFragment,
    key: { id: userId },
    strict: false,
  });

  const tenants = usersTenants?.tenants ?? [];
  const atCapacity = tenants.length >= MAX_TENANTS_PER_USER;

  const [addUserToTenant] = useMutation(AddUserToTenantMutation, {
    context: eluveAdminHasuraContext,
  });

  const [status, setStatus] = useState<ProvisionStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async ({ tenantId, targetPath }: RunArgs) => {
      setError(null);

      // Resolve membership against the awaited query result rather than the
      // fragment snapshot read during render. On admin pages where
      // UsersTenants has not been seeded, the snapshot can still be empty
      // when `run` is invoked — mis-classifying a provisioned user as a
      // non-member would fire insertTenantUsersOne against an existing row
      // and trip the unique constraint. Apollo dedupes this against the
      // in-flight query started on mount, so when the cache is already warm
      // it resolves synchronously.
      try {
        await apolloClient.query({
          query: getCommandPaletteUserTenantsQuery,
          variables: { userId },
          context: eluveAdminHasuraContext,
          fetchPolicy: 'cache-first',
        });
      } catch (e) {
        setStatus('error');
        const message = `Failed to load tenant membership: ${e instanceof Error ? e.message : String(e)}`;
        setError(message);
        toast.error('Failed to load tenant membership', {
          id: `membership-query-error-${tenantId}`,
          description: e instanceof Error ? e.message : String(e),
        });
        return;
      }
      const resolvedTenants =
        cacheUtils.readFragment({
          fragment: usersTenantsFragment,
          key: { id: userId },
          strict: false,
        })?.tenants ?? [];

      const isMember = resolvedTenants.some((t) => t.tenantId === tenantId);
      if (isMember) {
        setStatus('done');
        navigate(targetPath);
        return;
      }

      if (resolvedTenants.length >= MAX_TENANTS_PER_USER) {
        // Surface the cap via `atCapacity` AND a toast — callers that don't
        // render their own inline UI (e.g. the command palette, which closes
        // after `run` resolves) would otherwise see a silent no-op.
        toast.error('Cannot provision tenant', {
          id: 'provision-at-capacity',
          description:
            'You reached the maximum number of provisioned tenants allowed.',
        });
        return;
      }

      setStatus('provisioning');
      try {
        // Provisioned admins are transient internal accounts — exclude them
        // from usage reporting at insert so they never pollute tenant counts.
        await addUserToTenant({
          variables: {
            tenantId,
            userId,
            role: 'tenant-admin',
            isExcluded: true,
          },
        });
      } catch (e) {
        setStatus('error');
        const message = `Failed to provision tenant: ${e instanceof Error ? e.message : String(e)}`;
        setError(message);
        toast.error('Failed to provision tenant', {
          id: `provision-error-${tenantId}`,
          description: e instanceof Error ? e.message : String(e),
        });
        return;
      }

      setStatus('switching');
      let sessionResult;
      try {
        sessionResult = await apiClient.auth.createTenantSession({
          body: { tenantId },
        });
      } catch (e) {
        setStatus('error');
        const message = `Failed to switch tenant session: ${e instanceof Error ? e.message : String(e)}`;
        setError(message);
        toast.error('Failed to switch tenant session', {
          id: `tenant-session-error-${tenantId}`,
          description: e instanceof Error ? e.message : String(e),
        });
        return;
      }

      // ts-rest resolves declared non-2xx (e.g. 403) instead of throwing, so a
      // failed switch skips the catch above and must be handled explicitly.
      if (sessionResult.status !== 201) {
        setStatus('error');
        const message = `Failed to switch tenant session. Status: ${sessionResult.status}`;
        setError(message);
        toast.error('Failed to switch tenant session', {
          id: `tenant-session-error-${tenantId}`,
          description: message,
        });
        return;
      }

      cacheUtils.updateFragment(
        { fragment: usersTenantsFragment, key: { id: userId } },
        (existing) => {
          if (!existing) return existing;
          return produce(existing, (draft) => {
            draft.tenants.push({
              tenantId,
              __typename: 'TenantUsers' as const,
              userId,
              role: 'tenant-admin',
            });
          });
        },
      );

      setStatus('done');
      navigate(targetPath);
    },
    [addUserToTenant, apiClient, apolloClient, navigate, toast, userId],
  );

  const isMember = useCallback(
    (target: string) => tenants.some((t) => t.tenantId === target),
    [tenants],
  );

  return { run, status, error, atCapacity, isMember };
};
